import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { setAvailabilityMessageTemplate } from '../utils/vendorAvailability';
import { DEFAULT_AVAILABILITY_MESSAGE_TEMPLATE } from '../utils/availabilityMessage';
import {
  DEFAULT_GOOGLE_CUSTOMER_REVIEWS,
  isGoogleReviewBadgeActive,
  isInquiryOptInActive,
  isBackendSurveyActive,
  normalizeGoogleCustomerReviews,
} from '../utils/googleCustomerReviews';
import { normalizeHotpointPriceMatch } from '../utils/hotpointPriceMatch';

const StorefrontSettingsContext = createContext({
  productRequestEnabled: false,
  categoryGridLayout: 'grid',
  availabilityMessageTemplate: DEFAULT_AVAILABILITY_MESSAGE_TEMPLATE,
  googleCustomerReviews: DEFAULT_GOOGLE_CUSTOMER_REVIEWS,
  loaded: false,
  showProductRequest: false,
  showGoogleReviewBadge: false,
  showInquiryOptIn: false,
  showBackendSurvey: false,
  hotpointPriceMatch: normalizeHotpointPriceMatch(),
  showPriceMatch: false,
  imagekitPrimaryEnabled: false,
});

export function StorefrontSettingsProvider({ children }) {
  const [productRequestEnabled, setProductRequestEnabled] = useState(false);
  const [categoryGridLayout, setCategoryGridLayout] = useState('grid');
  const [availabilityMessageTemplate, setTemplateState] = useState(
    DEFAULT_AVAILABILITY_MESSAGE_TEMPLATE
  );
  const [googleCustomerReviews, setGoogleCustomerReviews] = useState(
    DEFAULT_GOOGLE_CUSTOMER_REVIEWS
  );
  const [hotpointPriceMatch, setHotpointPriceMatch] = useState(normalizeHotpointPriceMatch());
  const [imagekitPrimaryEnabled, setImagekitPrimaryEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [topbar, footer] = await Promise.all([
          api.get('/content/topbar').catch(() => null),
          api.get('/content/footer').catch(() => null),
        ]);
        if (!cancelled && topbar) {
          setProductRequestEnabled(topbar.productRequestEnabled !== false);
          setCategoryGridLayout(topbar.categoryGridLayout === 'carousel' ? 'carousel' : 'grid');
          setGoogleCustomerReviews(normalizeGoogleCustomerReviews(topbar.googleCustomerReviews));
          setHotpointPriceMatch(normalizeHotpointPriceMatch(topbar.hotpointPriceMatch));
          setImagekitPrimaryEnabled(topbar.imagekitPrimaryEnabled === true);
        }
        if (!cancelled && footer) {
          const tpl = String(footer.availabilityMessageTemplate || '').trim();
          const resolved = tpl || DEFAULT_AVAILABILITY_MESSAGE_TEMPLATE;
          setTemplateState(resolved);
          setAvailabilityMessageTemplate(resolved);
        }
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      productRequestEnabled,
      categoryGridLayout,
      availabilityMessageTemplate,
      googleCustomerReviews,
      hotpointPriceMatch,
      imagekitPrimaryEnabled,
      loaded,
      showProductRequest: loaded && productRequestEnabled,
      showGoogleReviewBadge: loaded && isGoogleReviewBadgeActive(googleCustomerReviews),
      showInquiryOptIn: loaded && isInquiryOptInActive(googleCustomerReviews),
      showBackendSurvey: loaded && isBackendSurveyActive(googleCustomerReviews),
      showPriceMatch: loaded && hotpointPriceMatch.enabled === true,
    }),
    [
      productRequestEnabled,
      categoryGridLayout,
      availabilityMessageTemplate,
      googleCustomerReviews,
      hotpointPriceMatch,
      imagekitPrimaryEnabled,
      loaded,
    ]
  );

  return (
    <StorefrontSettingsContext.Provider value={value}>
      {children}
    </StorefrontSettingsContext.Provider>
  );
}

export function useStorefrontSettings() {
  return useContext(StorefrontSettingsContext);
}
